import { Injectable } from '@angular/core';
import { TemplateModel } from '../models/email-sequence.model';

export interface VariableConversion {
  original: string;
  converted: string;
}

export interface TemplateValidationResult {
  isValid: boolean;
  platform: 'instantly' | 'smartlead' | 'apollo' | 'hubspot';
  invalidVariables: string[];
  conversions: VariableConversion[];
  convertedSubject?: string;
  convertedBody?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TemplateValidationService {

  // Common variable mappings between platforms
  private readonly variableMappings = {
    // Instantly (camelCase) -> Smartlead (snake_case)
    instantly_to_smartlead: {
      'firstName': 'first_name',
      'lastName': 'last_name',
      'companyName': 'company_name',
      'companyUrl': 'company_url',
      'jobTitle': 'job_title',
      'email': 'email',
      'phone': 'phone',
      'linkedinUrl': 'linkedin_url',
      'industry': 'industry',
      'location': 'location',
      'fullName': 'full_name'
    } as Record<string, string>,
    // Smartlead (snake_case) -> Instantly (camelCase)
    smartlead_to_instantly: {
      'first_name': 'firstName',
      'last_name': 'lastName',
      'company_name': 'companyName',
      'company_url': 'companyUrl',
      'job_title': 'jobTitle',
      'email': 'email',
      'phone': 'phone',
      'linkedin_url': 'linkedinUrl',
      'industry': 'industry',
      'location': 'location',
      'full_name': 'fullName'
    } as Record<string, string>,
    // Instantly (camelCase) -> HubSpot (contact.property_name)
    instantly_to_hubspot: {
      'firstName': 'contact.firstname',
      'lastName': 'contact.lastname',
      'companyName': 'contact.company',
      'companyUrl': 'contact.website',
      'jobTitle': 'contact.jobtitle',
      'email': 'contact.email',
      'phone': 'contact.phone',
      'linkedinUrl': 'contact.linkedin_url',
      'industry': 'contact.industry',
      'location': 'contact.city',
      'fullName': 'contact.fullname'
    } as Record<string, string>,
    // Smartlead (snake_case) -> HubSpot (contact.property_name)
    smartlead_to_hubspot: {
      'first_name': 'contact.firstname',
      'last_name': 'contact.lastname',
      'company_name': 'contact.company',
      'company_url': 'contact.website',
      'job_title': 'contact.jobtitle',
      'email': 'contact.email',
      'phone': 'contact.phone',
      'linkedin_url': 'contact.linkedin_url',
      'industry': 'contact.industry',
      'location': 'contact.city',
      'full_name': 'contact.fullname'
    } as Record<string, string>,
    // HubSpot (contact.property_name) -> Instantly (camelCase)
    hubspot_to_instantly: {
      'contact.firstname': 'firstName',
      'contact.lastname': 'lastName',
      'contact.company': 'companyName',
      'contact.website': 'companyUrl',
      'contact.jobtitle': 'jobTitle',
      'contact.email': 'email',
      'contact.phone': 'phone',
      'contact.linkedin_url': 'linkedinUrl',
      'contact.industry': 'industry',
      'contact.city': 'location',
      'contact.fullname': 'fullName'
    } as Record<string, string>,
    // HubSpot (contact.property_name) -> Smartlead (snake_case)
    hubspot_to_smartlead: {
      'contact.firstname': 'first_name',
      'contact.lastname': 'last_name',
      'contact.company': 'company_name',
      'contact.website': 'company_url',
      'contact.jobtitle': 'job_title',
      'contact.email': 'email',
      'contact.phone': 'phone',
      'contact.linkedin_url': 'linkedin_url',
      'contact.industry': 'industry',
      'contact.city': 'location',
      'contact.fullname': 'full_name'
    } as Record<string, string>
  };

  constructor() { }

  /**
   * Extract all template variables from text (format: {{variableName}})
   */
  private extractVariables(text: string): string[] {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      const variable = match[1].trim();
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }
    
    return variables;
  }

  /**
   * Check if a variable follows camelCase convention (for Instantly)
   */
  private isCamelCase(variable: string): boolean {
    // Should start with lowercase letter, no underscores, may contain numbers
    return /^[a-z][a-zA-Z0-9]*$/.test(variable) && !variable.includes('_');
  }

  /**
   * Check if a variable follows snake_case convention (for Smartlead)
   */
  private isSnakeCase(variable: string): boolean {
    // Should be all lowercase with underscores, may contain numbers
    return /^[a-z][a-z0-9_]*$/.test(variable) && variable.includes('_');
  }

  /**
   * Check if a variable follows HubSpot contact property format (contact.property_name)
   */
  private isHubSpotFormat(variable: string): boolean {
    // Should start with 'contact.' followed by lowercase property name, may contain underscores
    return /^contact\.[a-z][a-z0-9_]*$/.test(variable);
  }

  /**
   * Validate template variables against platform requirements
   */
  validateTemplate(template: TemplateModel, targetPlatform: 'instantly' | 'smartlead' | 'apollo' | 'hubspot'): TemplateValidationResult {
    const subjectVariables = this.extractVariables(template.subject || '');
    const bodyVariables = this.extractVariables(template.body || '');
    const allVariables = [...new Set([...subjectVariables, ...bodyVariables])];
    
    const invalidVariables: string[] = [];
    const conversions: VariableConversion[] = [];

    for (const variable of allVariables) {
      let isValid = false;
      
      if (targetPlatform === 'instantly') {
        isValid = this.isCamelCase(variable);
        if (!isValid && this.isSnakeCase(variable)) {
          // Try to convert from snake_case to camelCase
          const converted = this.variableMappings.smartlead_to_instantly[variable];
          if (converted) {
            conversions.push({ original: variable, converted });
          } else {
            // Fallback: convert snake_case to camelCase manually
            const camelCaseVar = variable.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
            conversions.push({ original: variable, converted: camelCaseVar });
          }
        } else if (!isValid && this.isHubSpotFormat(variable)) {
          // Try to convert from HubSpot format to camelCase
          const converted = this.variableMappings.hubspot_to_instantly[variable];
          if (converted) {
            conversions.push({ original: variable, converted });
          } else {
            // Fallback: convert contact.property_name to camelCase
            const propertyName = variable.replace('contact.', '');
            const camelCaseVar = propertyName.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
            conversions.push({ original: variable, converted: camelCaseVar });
          }
        }
      } else if (targetPlatform === 'smartlead') {
        isValid = this.isSnakeCase(variable) || variable === 'email'; // email is common to both
        if (!isValid && this.isCamelCase(variable)) {
          // Try to convert from camelCase to snake_case
          const converted = this.variableMappings.instantly_to_smartlead[variable];
          if (converted) {
            conversions.push({ original: variable, converted });
          } else {
            // Fallback: convert camelCase to snake_case manually
            const snakeCaseVar = variable.replace(/[A-Z]/g, (letter) => '_' + letter.toLowerCase());
            conversions.push({ original: variable, converted: snakeCaseVar });
          }
        } else if (!isValid && this.isHubSpotFormat(variable)) {
          // Try to convert from HubSpot format to snake_case
          const converted = this.variableMappings.hubspot_to_smartlead[variable];
          if (converted) {
            conversions.push({ original: variable, converted });
          } else {
            // Fallback: convert contact.property_name to snake_case
            const propertyName = variable.replace('contact.', '');
            conversions.push({ original: variable, converted: propertyName });
          }
        }
      } else if (targetPlatform === 'hubspot') {
        isValid = this.isHubSpotFormat(variable) || variable === 'email'; // email is common
        if (!isValid && this.isCamelCase(variable)) {
          // Try to convert from camelCase to HubSpot format
          const converted = this.variableMappings.instantly_to_hubspot[variable];
          if (converted) {
            conversions.push({ original: variable, converted });
          } else {
            // Fallback: convert camelCase to contact.property_name
            const snakeCaseVar = variable.replace(/[A-Z]/g, (letter) => '_' + letter.toLowerCase());
            conversions.push({ original: variable, converted: `contact.${snakeCaseVar}` });
          }
        } else if (!isValid && this.isSnakeCase(variable)) {
          // Try to convert from snake_case to HubSpot format
          const converted = this.variableMappings.smartlead_to_hubspot[variable];
          if (converted) {
            conversions.push({ original: variable, converted });
          } else {
            // Fallback: convert snake_case to contact.property_name
            conversions.push({ original: variable, converted: `contact.${variable}` });
          }
        }
      }
      
      if (!isValid && conversions.find(c => c.original === variable) === undefined) {
        invalidVariables.push(variable);
      }
    }

    // Generate converted content if we have conversions
    let convertedSubject: string | undefined;
    let convertedBody: string | undefined;
    
    if (conversions.length > 0) {
      convertedSubject = template.subject || '';
      convertedBody = template.body || '';
      
      for (const conversion of conversions) {
        const regex = new RegExp(`\\{\\{${conversion.original}\\}\\}`, 'g');
        convertedSubject = convertedSubject.replace(regex, `{{${conversion.converted}}}`);
        convertedBody = convertedBody.replace(regex, `{{${conversion.converted}}}`);
      }
    }

    return {
      isValid: invalidVariables.length === 0 && conversions.length === 0,
      platform: targetPlatform,
      invalidVariables,
      conversions,
      convertedSubject,
      convertedBody
    };
  }

  /**
   * Get a suggested template name for the converted template
   */
  getConvertedTemplateName(originalName: string, targetPlatform: 'instantly' | 'smartlead' | 'hubspot'): string {
    const platformPrefix = targetPlatform === 'instantly' ? 'Instantly' : 
                          targetPlatform === 'smartlead' ? 'Smartlead' : 'HubSpot';
    
    // Check if the name already has a platform prefix to avoid duplication
    if (originalName.toLowerCase().startsWith(platformPrefix.toLowerCase())) {
      return originalName;
    }
    
    return `${platformPrefix} - ${originalName}`;
  }

  /**
   * Check if template needs conversion for the target platform
   */
  needsConversion(template: TemplateModel, targetPlatform: 'instantly' | 'smartlead' | 'hubspot'): boolean {
    const validation = this.validateTemplate(template, targetPlatform);
    return validation.conversions.length > 0;
  }
}